// src/ConsumerApp/Screens/Home/components/LiveOrderTrackerBanner.js

import React from 'react';
import { useNavigate } from 'react-router-dom';

// Real SVGs from assets/icons
import Truck from '../../../../assets/icons/Truck.svg';
import CaretRight from '../../../../assets/icons/CaretRight.svg';

export default function LiveOrderTrackerBanner({ activeOrder }) {
  const navigate = useNavigate();

  if (!activeOrder) return null;

  const orderId = activeOrder.order_id || activeOrder._id;
  const orderNumber = activeOrder.order_number || 'Live Order';
  const status = activeOrder.status || 'Confirmed';
  const totalAmount = Number(activeOrder.total || 0);
  const itemsCount = activeOrder.items_count || activeOrder.items?.length || 0;

  // Status-driven copy
  const isDelivering =
    status.toLowerCase().includes('delivering') ||
    status.toLowerCase().includes('out') ||
    status.toLowerCase().includes('way');

  const statusHeading = isDelivering ? 'Out for Delivery' : 'Order Confirmed';
  const statusSubtext = isDelivering
    ? 'Vehicle is en route to your site'
    : 'Your materials are packed & awaiting vehicle dispatch';

  return (
    <>
      {/* Scoped CSS media queries for bulletproof responsiveness */}
      <style>{`
        .bb-live-order-banner {
          background-color: #1E293B;
          border-radius: 14px;
          padding: 14px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          box-shadow: 0 4px 18px rgba(15, 23, 42, 0.22);
          margin-bottom: 20px;
          width: 100%;
          box-sizing: border-box;
          border: 1px solid #334155;
          gap: 14px;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }

        .bb-live-order-banner:hover {
          box-shadow: 0 6px 22px rgba(15, 23, 42, 0.32);
        }

        .bb-live-left {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
          min-width: 0;
        }

        .bb-live-icon-circle {
          width: 40px;
          height: 40px;
          border-radius: 20px;
          background-color: #334155;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border: 1px solid #475569;
        }

        .bb-live-truck-icon {
          width: 18px;
          height: 18px;
          filter: brightness(0) invert(1);
          display: block;
        }

        .bb-live-text-cluster {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }

        .bb-live-status-row {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }

        .bb-live-pulse-dot {
          width: 8px;
          height: 8px;
          border-radius: 4px;
          background-color: #22C55E;
          box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.25);
          display: inline-block;
          flex-shrink: 0;
        }

        .bb-live-heading {
          margin: 0;
          font-size: 14.5px;
          font-weight: 800;
          color: #F8FAFC;
          letter-spacing: 0.2px;
          white-space: nowrap;
        }

        .bb-live-order-badge {
          font-size: 11.5px;
          font-weight: 600;
          color: #94A3B8;
        }

        .bb-live-subtext {
          font-size: 12px;
          color: #CBD5E1;
          line-height: 16px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .bb-live-right {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-shrink: 0;
        }

        .bb-live-meta-col {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 1px;
        }

        .bb-live-meta-items {
          font-size: 10.5px;
          font-weight: 600;
          color: #94A3B8;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }

        .bb-live-meta-price {
          font-size: 13.5px;
          font-weight: 700;
          color: #E2E8F0;
        }

        .bb-live-track-btn {
          background-color: #E9001E;
          color: #FFFFFF;
          border: none;
          border-radius: 8px;
          padding: 8px 14px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 5px;
          box-shadow: 0 2px 8px rgba(233, 0, 30, 0.35);
          white-space: nowrap;
        }

        /* ── TABLET BREAKPOINT (<= 768px) ── */
        @media (max-width: 768px) {
          .bb-live-order-banner {
            padding: 12px 14px;
            gap: 10px;
          }
          .bb-live-right {
            gap: 12px;
          }
          .bb-live-subtext {
            font-size: 11.5px;
          }
        }

        /* ── MOBILE BREAKPOINT (<= 480px) ── */
        @media (max-width: 480px) {
          .bb-live-order-banner {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
            padding: 14px;
          }

          .bb-live-left {
            width: 100%;
          }

          .bb-live-subtext {
            white-space: normal;
          }

          .bb-live-right {
            width: 100%;
            justify-content: space-between;
            border-top: 1px solid #334155;
            padding-top: 10px;
          }

          .bb-live-meta-col {
            align-items: flex-start;
          }

          .bb-live-track-btn {
            padding: 7px 14px;
            font-size: 11.5px;
          }
        }
      `}</style>

      <div
        className="bb-live-order-banner"
        onClick={() => navigate(`/consumer/account/orders/${orderId}`)}
      >
        {/* Left Content: Live Status & Vehicle Icon */}
        <div className="bb-live-left">
          <div className="bb-live-icon-circle">
            <img src={Truck} alt="Truck" className="bb-live-truck-icon" />
          </div>

          <div className="bb-live-text-cluster">
            <div className="bb-live-status-row">
              <span className="bb-live-pulse-dot" />
              <h4 className="bb-live-heading">{statusHeading}</h4>
              <span className="bb-live-order-badge">#{orderNumber}</span>
            </div>
            <span className="bb-live-subtext">{statusSubtext}</span>
          </div>
        </div>

        {/* Right Content: Subdued Meta (Price + Items) & Red CTA */}
        <div className="bb-live-right">
          <div className="bb-live-meta-col">
            <span className="bb-live-meta-items">
              {itemsCount} {itemsCount === 1 ? 'item' : 'items'}
            </span>
            <span className="bb-live-meta-price">₹{totalAmount.toLocaleString('en-IN')}</span>
          </div>

          <button
            type="button"
            className="bb-live-track-btn"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/consumer/account/orders/${orderId}`);
            }}
          >
            <span>Track</span>
            <img
              src={CaretRight}
              alt="→"
              style={{ width: 11, height: 11, filter: 'brightness(0) invert(1)' }}
            />
          </button>
        </div>
      </div>
    </>
  );
}